import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import MultiEmailField from '../components/MultiEmailField';
import MultiIbanField from '../components/MultiIbanField';
import POTEditor from '../components/clients/POTEditor';
import SpvSeriesPanel from '../components/clients/SpvSeriesPanel';
import { getBankNameFromIbans } from '../utils/bankMapping';
import { PageHeader } from '../components/Navigation';
import { getBackPath } from '../utils/navigation';
import { useClientDetail, useClientInvoices, useClientEndUsers, useClientProducts } from '../hooks/useClients';
import { apiFetch } from '../utils/api';
import '../InvoiceDetail.css';
import '../Clients.css';

// NEW: A reusable message component to display feedback (like the success pop-up)
const FormMessage = ({ message, onClear }) => {
  // This effect sets a timer to clear the message after 5 seconds.
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClear(); // Callback to clear the message state in the parent component.
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, onClear]);

  if (!message) return null;
  const messageClass = message.type === 'error' 
    ? 'validation-message validation-message-error' 
    : 'validation-message validation-message-success';

  return (
    <div className={messageClass}>
      <p className="validation-message-text">{message.text}</p>
    </div>
  );
};

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = getBackPath(location, '/clients');

  // State to hold the message passed via navigation.
  const [message, setMessage] = useState(location.state?.message);
  
  // NEW: This effect clears the message from the browser's history state
  // to prevent it from reappearing if the user refreshes or navigates back.
  useEffect(() => {
    if (location.state?.message) {
      navigate(location.pathname, { state: {}, replace: true });
    }
  }, [location, navigate]);

  const { data, status, error, refetch } = useClientDetail(id);
  const [activeTab, setActiveTab] = React.useState('invoices');
  const [pageInv, setPageInv] = React.useState(1);
  const [pageEnd, setPageEnd] = React.useState(1);
  const [pageProd, setPageProd] = React.useState(1);
  const inv = useClientInvoices(id, { page: pageInv, pageSize: 10 });
  const endUsers = useClientEndUsers(id, { page: pageEnd, pageSize: 10 });
  const products = useClientProducts(id, { page: pageProd, pageSize: 10 });

  const [detail, setDetail] = React.useState(null);
  React.useEffect(() => {
    if (status === 'success') setDetail(data);
  }, [status, data, id]);

  const [isEditing, setIsEditing] = React.useState(false);
  const [form, setForm] = React.useState(null);
  const [saveError, setSaveError] = React.useState(null);

  if (status === 'loading' || status === 'idle') return <div className="invoice-detail"><main className="invoice-detail-main">Se încarcă...</main></div>;
  if (status === 'error') return <div className="invoice-detail"><main className="invoice-detail-main" style={{ color: '#b91c1c' }}>{error}</main></div>;
  if (!data) return null;

  const current = detail || data;
  const { client, stats } = current;

  const startEdit = () => {
    setForm({
      nume: client.nume || '',
      cui: client.cui || '',
      email: client.email || '',
      nr_reg_com: client.nr_reg_com || '',
      iban: client.iban || '',
      numeBanca: client.numeBanca || '',
      adresa: client.adresa || '',
      oras: client.oras || '',
      judet: client.judet || '',
      tara_cod: client.tara_cod || 'RO',
      tva: String((client.tva ?? 21)),
      last_invoice_number: client.last_invoice_number || '',
    });
    setIsEditing(true);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setForm(null);
    setSaveError(null);
  };

  const handleChange = (field, value) => {
    setForm((f) => {
      const updated = { ...f, [field]: value };
      // Auto-fill bank name when IBAN changes
      if (field === 'iban') {
        const detectedBank = getBankNameFromIbans(value);
        if (detectedBank) {
          updated.numeBanca = detectedBank;
        }
      }
      return updated;
    });
  };

  const saveEdit = async () => {
    setSaveError(null);
    const oldTva = client.tva;
    try {
      const res = await apiFetch(`/api/clients/${client.id}`, {
        method: 'PATCH',
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.msg || 'Eroare la salvare.');
      }
      const json = await res.json();

      // Check if VAT changed - if yes, reload page to refresh all invoices
      const newTva = form.tva;
      if (oldTva !== newTva) {
        // VAT changed - force full page reload to refresh invoices (silent reload)
        window.location.reload();
      } else {
        // Normal update without VAT change
        setDetail(json);
        setIsEditing(false);
        setForm(null);
      }
    } catch (e) {
      setSaveError(e.message);
    }
  };

  const breadcrumbs = [
    { label: 'Validare', to: '/validation-hub' },
    { label: 'Emitenți', to: '/clients' },
    { label: client.nume || 'Client', to: null }
  ];

  return (
    <div className="invoice-detail">
      <PageHeader
        backTo={backPath}
        backLabel="Înapoi la Emitenți"
        title={client.nume || 'Client fără nume'}
        subtitle={`CUI: ${client.cui || '—'} • Email: ${Array.isArray(client.email) ? client.email.join(', ') : (client.email || '—')}`}
        breadcrumbs={breadcrumbs}
      />

      <main className="invoice-detail-main">
        <div className="invoice-detail-content">
          {/* NEW: The message from the Product Create Page will appear here */}
          <FormMessage message={message} onClear={() => setMessage(null)} />

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Profil Emitent</h2>
              <p className="section-subtitle">Informații de identificare și adresă</p>
              <div style={{ marginTop: '0.5rem' }}>
                {!isEditing ? (
                  <button className="review-btn" onClick={startEdit}>Editează</button>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="review-btn" onClick={cancelEdit}>Renunță</button>
                    <button className="review-btn" onClick={saveEdit}>Salvează</button>
                  </div>
                )}
              </div>
            </div>
            <div className="section-content">
              {!isEditing ? (
              <div className="form-grid form-grid-2">
                <Field label="Denumire" value={client.nume || '—'} />
                <Field label="CUI" value={client.cui || '—'} mono />
                <div style={{ gridColumn: '1 / -1' }}>
                  <MultiEmailField emails={client.email} onChange={() => {}} label="Email" readOnly={true} />
                </div>
                <Field label="Nr. Reg. Com." value={client.nr_reg_com || '—'} mono />
                <div style={{ gridColumn: '1 / -1' }}>
                  <MultiIbanField ibans={client.iban} onChange={() => {}} label="IBAN" readOnly={true} />
                </div>
                <Field label="Bancă" value={client.numeBanca || '—'} />
                <Field label="Număr Ultima Factură" value={client.last_invoice_number || '—'} mono />
                <Field label="Țară" value={client.tara_cod || '—'} />
                <Field label="Județ" value={client.judet || '—'} />
                <Field label="Oraș" value={client.oras || '—'} />
                <Field label="Adresă" value={client.adresa || '—'} full />
                <Field label="TVA Implicit (%)" value={String(client.tva ?? '—')} />
              </div>
            ) : (
              <div className="form-grid form-grid-2">
                <InputField label="Denumire" value={form.nume} onChange={(v) => handleChange('nume', v)} />
                <InputField label="CUI" value={form.cui} onChange={(v) => handleChange('cui', v)} mono />
                <div style={{ gridColumn: '1 / -1' }}>
                  <MultiEmailField emails={form.email} onChange={(v) => handleChange('email', v)} label="Email" readOnly={false} />
                </div>
                <InputField label="Nr. Reg. Com." value={form.nr_reg_com} onChange={(v) => handleChange('nr_reg_com', v)} mono />
                <div style={{ gridColumn: '1 / -1' }}>
                  <MultiIbanField ibans={form.iban} onChange={(v) => handleChange('iban', v)} label="IBAN" readOnly={false} />
                </div>
                <InputField label="Bancă" value={form.numeBanca} onChange={(v) => handleChange('numeBanca', v)} placeholder="Se completează automat din IBAN" />
                <InputField label="Număr Ultima Factură" value={form.last_invoice_number} onChange={(v) => handleChange('last_invoice_number', v)} mono placeholder="ex: FAC-2024-001" />
                <InputField label="Țară" value={form.tara_cod} onChange={(v) => handleChange('tara_cod', v)} />
                <InputField label="Județ" value={form.judet} onChange={(v) => handleChange('judet', v)} />
                <InputField label="Oraș" value={form.oras} onChange={(v) => handleChange('oras', v)} />
                <InputField label="Adresă" value={form.adresa} onChange={(v) => handleChange('adresa', v)} full />
                <InputField label="TVA Implicit (%)" value={form.tva} onChange={(v) => handleChange('tva', v)} />
                </div>
              )}
              {saveError && <div style={{ color: '#b91c1c', marginTop: '0.5rem' }}>{saveError}</div>}
              {client.is_placeholder && (
                <div style={{ marginTop: '1rem' }}>
                  <span className="clients-badge-placeholder">Necomplet</span>
                </div>
              )}
            </div>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Sumar</h2>
              <p className="section-subtitle">Activitate recentă și totaluri</p>
            </div>
            <div className="section-content">
              <div className="stats-grid">
                <button
                  className={`stat-card stat-card-clickable ${activeTab === 'invoices' ? 'stat-card-active' : ''}`}
                  onClick={() => setActiveTab('invoices')}
                  aria-pressed={activeTab === 'invoices'}
                >
                  <div className="stat-card-label">Facturi</div>
                  <div className="stat-card-value">{stats.invoices_total}</div>
                </button>
                <button
                  className={`stat-card stat-card-clickable ${activeTab === 'endusers' ? 'stat-card-active' : ''}`}
                  onClick={() => setActiveTab('endusers')}
                  aria-pressed={activeTab === 'endusers'}
                >
                  <div className="stat-card-label">Beneficiari</div>
                  <div className="stat-card-value">{stats.end_users_total}</div>
                </button>
                <button
                  className={`stat-card stat-card-clickable ${activeTab === 'products' ? 'stat-card-active' : ''}`}
                  onClick={() => setActiveTab('products')}
                  aria-pressed={activeTab === 'products'}
                >
                  <div className="stat-card-label">Articole</div>
                  <div className="stat-card-value">{stats.products_total}</div>
                </button>
                <button
                  className={`stat-card stat-card-clickable ${activeTab === 'pot' ? 'stat-card-active' : ''}`}
                  onClick={() => setActiveTab('pot')}
                  aria-pressed={activeTab === 'pot'}
                >
                  <div className="stat-card-label">AI Rules (POT)</div>
                  <div className="stat-card-value">{client.packet_of_truth ? 'Active' : 'Empty'}</div>
                </button>
                <button
                  className={`stat-card stat-card-clickable ${activeTab === 'spv-series' ? 'stat-card-active' : ''}`}
                  onClick={() => setActiveTab('spv-series')}
                  aria-pressed={activeTab === 'spv-series'}
                >
                  <div className="stat-card-label">Serii SPV (verificat la)</div>
                  <div className="stat-card-value">
                    {client.spv_series?.last_sync_at
                      ? new Date(client.spv_series.last_sync_at).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' })
                      : '—'}
                  </div>
                </button>
                <div className="stat-card">
                  {(() => {
                    // SPV is the numbering authority: show the last number ANAF
                    // knows; the legacy app-sent counter only as fallback
                    const entries = Object.values(client.spv_series?.series || {});
                    const spvLast = entries.length
                      ? entries.reduce((a, b) => ((a.last_seen_at || '') > (b.last_seen_at || '') ? a : b))
                      : null;
                    return (
                      <>
                        <div className="stat-card-label">{spvLast ? 'Ultimul număr (SPV)' : 'Ultima factură'}</div>
                        <div className="stat-card-value">{spvLast?.last_number || client.last_invoice_number || '—'}</div>
                      </>
                    );
                  })()}
                </div>
                <div className="stat-card">
                  <div className="stat-card-label">Total vânzări</div>
                  <div className="stat-card-value">{`${(stats.total_amount?.toFixed ? stats.total_amount.toFixed(2) : stats.total_amount)} RON`}</div>
                </div>
              </div>
            </div>
          </div>

          <TabSection
            activeTab={activeTab}
            inv={inv}
            endUsers={endUsers}
            products={products}
            onPageInv={setPageInv}
            onPageEnd={setPageEnd}
            onPageProd={setPageProd}
            clientId={id}
            onUpdate={refetch}
          />
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, full = false, mono = false }) {
  return (
    <div className={full ? 'form-field party-address' : 'form-field'}>
      <div className="form-field-label">{label}</div>
      <div className={`form-field-value ${mono ? 'form-field-value-mono' : ''}`}>{value}</div>
    </div>
  );
}

function InputField({ label, value, onChange, full = false, mono = false, placeholder = '' }) {
  return (
    <div className={full ? 'form-field party-address' : 'form-field'}>
      <div className="form-field-label">{label}</div>
      <input
        className={`clients-input ${mono ? 'form-field-value-mono' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function TabSection({ activeTab, inv, endUsers, products, onPageInv, onPageEnd, onPageProd, clientId, onUpdate }) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDelete, setToDelete] = React.useState(null);
  const [confirmEUOpen, setConfirmEUOpen] = React.useState(false);
  const [toDeleteEU, setToDeleteEU] = React.useState(null);
  
  if (activeTab === 'invoices') {
    return (
      <div className="section-card">
        <div className="section-header">
          <h2 className="section-title">Facturi</h2>
          <p className="section-subtitle">Toate facturile emitentului</p>
        </div>
        <div className="section-content">
          {inv.status === 'loading' && 'Se încarcă...'}
          {inv.status === 'error' && <div style={{ color: '#b91c1c' }}>{inv.error}</div>}
          {inv.status === 'success' && (
            <>
              {/* CORRECTED CLASS NAMES HERE */}
              <div className="lines-table-container">
                <table className="lines-table">
                  <thead className="lines-table-head">
                    <tr>
                      <th>Număr</th>
                      <th>Data</th>
                      <th>Cumpărător</th>
                      <th className="text-right">Total</th>
                      <th>Trimitere</th>
                      <th>Stare</th>
                    </tr>
                  </thead>
                  <tbody className="lines-table-body">
                    {inv.data.items.map((it) => (
                      <tr key={it.id} className="table-row" onClick={() => navigate(`/invoice/${it.id}`, { state: { from: 'client', clientId } })} style={{ cursor: 'pointer' }}>
                        <td>{it.numar_factura}</td>
                        <td>{it.data_emitere || '—'}</td>
                        <td>{it.end_user_name || '—'}</td>
                        <td className="lines-table-number">{it.total_general?.toFixed ? it.total_general.toFixed(2) : it.total_general}</td>
                        <td>
                          {it.stare_trimitere === 'trimisa'
                            ? <span style={{ padding: '0.15rem 0.6rem', borderRadius: 12, background: '#d1fae5', color: '#065f46', fontSize: '0.8rem' }}>Trimisă ANAF</span>
                            : <span style={{ padding: '0.15rem 0.6rem', borderRadius: 12, background: '#fef3c7', color: '#92400e', fontSize: '0.8rem' }}>Draft</span>}
                        </td>
                        <td>{it.confidence_flag}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="pagination">
                <div>Pagina {inv.data.page} din {Math.max(1, Math.ceil(inv.data.total / inv.data.page_size))}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="review-btn" disabled={inv.data.page <= 1} onClick={() => onPageInv(Math.max(1, inv.data.page - 1))}>Înapoi</button>
                  <button className="review-btn" disabled={inv.data.page * inv.data.page_size >= inv.data.total} onClick={() => onPageInv(inv.data.page + 1)}>Înainte</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (activeTab === 'endusers') {
    return (
      <div className="section-card">
        <div className="section-header">
          <h2 className="section-title">Beneficiari</h2>
          <p className="section-subtitle">Beneficiarii acestui emitent</p>
          <div>
            <Link to={`/clients/${clientId}/end-users/new`} className="review-btn">Adaugă beneficiar</Link>
          </div>
        </div>
        <div className="section-content">
          {endUsers.status === 'loading' && 'Se încarcă...'}
          {endUsers.status === 'error' && <div style={{ color: '#b91c1c' }}>{endUsers.error}</div>}
          {endUsers.status === 'success' && (
            <>
              <div className="lines-table-container">
                <table className="lines-table">
                  <thead className="lines-table-head">
                    <tr>
                      <th>Nume</th><th>CUI / CNP</th><th className="text-right">Facturi</th><th>Tip</th><th>Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody className="lines-table-body">
                    {endUsers.data.items.map((e) => (
                      <tr key={e.id} className="table-row" onClick={() => navigate(`/end-users/${e.id}`)} style={{ cursor: 'pointer' }}>
                        <td>{e.nume || '—'}</td>
                        {/* --- THIS IS THE FIX --- */}
                        {/* Display the CNP if it exists, otherwise fall back to the CUI */}
                        <td>{e.cnp || e.cui || '—'}</td>
                        <td className="lines-table-number">{e.invoices_count}</td>
                        <td>{e.is_placeholder ? 'Necomplet' : 'Complet'}</td>
                        <td onClick={(ev) => ev.stopPropagation()}>
                          <button className="review-btn" onClick={() => { setToDeleteEU({ id: e.id, name: e.nume }); setConfirmEUOpen(true); }}>Șterge</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="pagination">
                <div>Pagina {endUsers.data.page} din {Math.max(1, Math.ceil(endUsers.data.total / endUsers.data.page_size))}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="review-btn" disabled={endUsers.data.page <= 1} onClick={() => onPageEnd(Math.max(1, endUsers.data.page - 1))}>Înapoi</button>
                  <button className="review-btn" disabled={endUsers.data.page * endUsers.data.page_size >= endUsers.data.total} onClick={() => onPageEnd(endUsers.data.page + 1)}>Înainte</button>
                </div>
              </div>
              <ConfirmDialog
                open={confirmEUOpen}
                title="Ștergere Beneficiar"
                message={`Ștergeți beneficiarul "${toDeleteEU?.name || '—'}"? Această acțiune este ireversibilă.`}
                confirmText="Șterge"
                cancelText="Anulează"
                onCancel={() => { setConfirmEUOpen(false); setToDeleteEU(null); }}
                onConfirm={async () => {
                  try {
                    const res = await apiFetch(`/api/end-users/${toDeleteEU.id}`, { method: 'DELETE' });
                    if (!res.ok) {
                      const err = await res.json().catch(() => ({}));
                      throw new Error(err.msg || 'Eroare la ștergere');
                    }
                    setConfirmEUOpen(false);
                    setToDeleteEU(null);
                    window.location.reload();
                  } catch (e) {
                    alert(e.message);
                    setConfirmEUOpen(false);
                    setToDeleteEU(null);
                  }
                }}
              />
            </>
          )}
        </div>
      </div>
    );
  }

  if (activeTab === 'pot') {
    return <POTEditor clientId={clientId} onUpdate={onUpdate} />;
  }

  if (activeTab === 'spv-series') {
    return <SpvSeriesPanel clientId={clientId} />;
  }

  return (
    <div className="section-card">
      <div className="section-header">
        <h2 className="section-title">Articole</h2>
        <p className="section-subtitle">Articolele configurate pentru acest emitent</p>
        <div>
          <Link to={`/clients/${clientId}/products/new`} className="review-btn">Adaugă articol</Link>
        </div>
      </div>
      <div className="section-content">
        {products.status === 'loading' && 'Se încarcă...'}
        {products.status === 'error' && <div style={{ color: '#b91c1c' }}>{products.error}</div>}
        {products.status === 'success' && (
          <>
            {/* CORRECTED CLASS NAMES HERE */}
            <div className="lines-table-container">
                <table className="lines-table">
                  <thead className="lines-table-head">
                    <tr>
                    <th>Denumire</th><th>U.M.</th><th className="text-right">Preț Net</th><th>Monedă</th><th>Cant. Impl.</th><th>TVA</th><th>Status</th><th>Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody className="lines-table-body">
                    {products.data.items.map((p) => (
                      <tr key={p.id} className="table-row" onClick={() => navigate(`/products/${p.id}`)} style={{ cursor: 'pointer' }}>
                        <td>{p.nume}</td>
                        <td>{p.unitate_masura}</td>
                        <td className="lines-table-number">{p.pret_unitar_net?.toFixed ? p.pret_unitar_net.toFixed(2) : p.pret_unitar_net}</td>
                        <td>{p.moneda || 'RON'}</td>
                        <td className="lines-table-number">{p.cantitate_implicita?.toFixed ? p.cantitate_implicita.toFixed(2) : (p.cantitate_implicita || 1)}</td>
                        <td>{p.tva_procent}%</td>
                        <td>
                          {p.valid ? (
                            <span className="product-badge-valid">Validat</span>
                          ) : (
                            <span className="product-badge-pending">În curs</span>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              className="review-btn"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                navigate(`/products/${p.id}`);
                              }}
                            >
                              Editează
                            </button>
                            <button
                              className="review-btn"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setToDelete({ id: p.id, name: p.nume });
                                setConfirmOpen(true);
                              }}
                            >
                              Șterge
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <div>Pagina {products.data.page} din {Math.max(1, Math.ceil(products.data.total / products.data.page_size))}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="review-btn" disabled={products.data.page <= 1} onClick={() => onPageProd(Math.max(1, products.data.page - 1))}>Înapoi</button>
                <button className="review-btn" disabled={products.data.page * products.data.page_size >= products.data.total} onClick={() => onPageProd(products.data.page + 1)}>Înainte</button>
              </div>
            </div>
            {confirmOpen && (
              <div className="modal-overlay" onClick={() => { setConfirmOpen(false); setToDelete(null); }}>
                <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Ștergere Articol</h3>
                  </div>
                  <div className="modal-body">
                    <p>Ștergeți articolul "{toDelete?.name}"? Această acțiune este ireversibilă.</p>
                  </div>
                  <div className="modal-actions">
                    <button className="review-btn" onClick={() => { setConfirmOpen(false); setToDelete(null); }}>Anulează</button>
                    <button className="review-btn modal-danger" onClick={async () => {
                      try {
                        const res = await apiFetch(`/api/products/${toDelete.id}`, { method: 'DELETE' });
                        if (!res.ok) {
                          const err = await res.json().catch(() => ({}));
                          throw new Error(err.msg || 'Eroare la ștergere');
                        }
                        setConfirmOpen(false);
                        setToDelete(null);
                        window.location.reload();
                      } catch (e) {
                        alert(e.message);
                      }
                    }}>Șterge</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
